import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "context";
import { QueryKeys } from "queryClient";
import { ProductShortened } from "types";
import {
  getProductListByExpansionWithNoDeStock,
  getProductListByExpansionWithNoEnStock,
} from "modules/products/api";
import { useExpansionListForBuyingProducts } from "../../expansion/useExpansionListForBuyingProducts";
import { useProductListFilterAndSortForBuying } from "../useProductListFilterAndSortForBuying";

export const useProductListForBuying = (filter = true, useAlias = true) => {
  const filterAndSortProducts = useProductListFilterAndSortForBuying(
    filter,
    useAlias,
  );

  const { isAuth, isLoading: isLoadingAuth } = useAuthContext();

  const { expansionList, isLoading: isLoadingExpansions } =
    useExpansionListForBuyingProducts();

  const expansionIDList = useMemo(
    () =>
      new Set(expansionList.map((expansion) => expansion.id))
        .values()
        .toArray(),
    [expansionList],
  );

  const { data, isLoading } = useQuery({
    queryKey: [QueryKeys.Products, "forBuying", expansionIDList],
    queryFn: async () => {
      if (expansionIDList.length === 0) {
        return [];
      }

      console.log("expansionIDs: ", expansionIDList);

      const productListNoDeStockPromises = expansionIDList.map(
        (expansionIDItem) =>
          getProductListByExpansionWithNoDeStock(isAuth, expansionIDItem),
      );

      const productListNoDeStockResults = await Promise.all(
        productListNoDeStockPromises,
      );

      const productListNoDeStock = productListNoDeStockResults.reduce(
        (acc, productList) => [...acc, ...productList],
        [],
      );

      const productListNoEnStockPromises = expansionIDList.map(
        (expansionIDItem) =>
          getProductListByExpansionWithNoEnStock(isAuth, expansionIDItem),
      );

      const productListNoEnStockResults = await Promise.all(
        productListNoEnStockPromises,
      );

      const productListNoEnStock = productListNoEnStockResults.reduce(
        (acc, productList) => [...acc, ...productList],
        [],
      );

      const productList: ProductShortened[] = [];

      for (const product of productListNoDeStock) {
        const isInNoEnStockList = productListNoEnStock.find(
          (p) => p.id === product.id,
        );
        if (isInNoEnStockList) {
          productList.push(product);
        }
      }

      return productList;
    },
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    enabled: !isLoadingExpansions && !isLoadingAuth,
  });

  const productList = useMemo(
    () => filterAndSortProducts(data ?? []),
    [filterAndSortProducts, data],
  );

  return {
    productList,
    allProductList: data ?? [],
    isLoading,
  };
};
